import { localStore } from "@/lib/webStorage";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

/** Keep in sync with the pre-paint script in `index.html`. */
export const THEME_STORAGE_KEY = "flitrip.theme";

const DARK_QUERY = "(prefers-color-scheme: dark)";

function isThemePreference(value: string | null): value is ThemePreference {
  return value === "light" || value === "dark" || value === "system";
}

export function readThemePreference(): ThemePreference {
  const stored = localStore.getItem(THEME_STORAGE_KEY);
  return isThemePreference(stored) ? stored : "system";
}

export function writeThemePreference(preference: ThemePreference): void {
  localStore.setItem(THEME_STORAGE_KEY, preference);
}

export function systemPrefersDark(): boolean {
  return window.matchMedia(DARK_QUERY).matches;
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === "system") {
    return systemPrefersDark() ? "dark" : "light";
  }
  return preference;
}

/** Toggles the `dark` class on `<html>` without animating every color. */
export function applyResolvedTheme(theme: ResolvedTheme): void {
  const root = document.documentElement;
  if (root.classList.contains("dark") === (theme === "dark")) {
    return;
  }
  root.classList.add("theme-switching");
  root.classList.toggle("dark", theme === "dark");
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      root.classList.remove("theme-switching");
    });
  });
}

export function subscribeToSystemTheme(onChange: () => void): () => void {
  const media = window.matchMedia(DARK_QUERY);
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}

/**
 * Current value of a `theme.css` custom property (e.g. `--color-surface-500`)
 * for canvas renderers that cannot resolve `var()`. Re-read after a theme change.
 */
export function readThemeToken(name: `--${string}`): string {
  // Multi-line declarations (font stacks) keep their line breaks: collapse them.
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .replace(/\s+/g, " ")
    .trim();
  if (!value) {
    throw new Error(`Theme token ${name} is not defined`);
  }
  return value;
}
